import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { alpha } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import colors from '../assets/theme/colors';
import CareSourceStatusRow from '../features/careSourceProcessing/CareSourceStatusRow';
import {
  getCareSourceScopeTitle,
  normalizeCareSourceScope,
} from '../features/careSourceProcessing/careSourceScope';
import {
  getCareSourceDisplayStatus,
  getCareSourceTitle,
} from '../features/careSourceProcessing/careSourceStatusModel';
import { useCareSourceHistory } from '../features/careSourceProcessing/useCareSourceHistory';
import { useChildContext } from '../contexts/ChildContext';
import { useRole } from '../contexts/RoleContext';
import { CARE_PROFILE_ROUTE } from '../constants/profileRoutes';

const statusChipStyle = (tone = 'info') => {
  if (tone === 'success') {
    return {
      color: '#2F6B4F',
      bgcolor: alpha('#2F8F67', 0.12),
    };
  }

  if (tone === 'warning') {
    return {
      color: '#A86A00',
      bgcolor: alpha('#D08A00', 0.14),
    };
  }

  return {
    color: colors.brand.deep,
    bgcolor: alpha(colors.brand.tint, 0.14),
  };
};

const SourceHistoryPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentChildId, setCurrentChildId } = useChildContext();
  const { childrenWithAccess = [] } = useRole();
  const selectedChild = childrenWithAccess.find((child) => child.id === currentChildId)
    || childrenWithAccess[0]
    || null;
  const selectedChildId = selectedChild?.id || '';
  const sourceScope = normalizeCareSourceScope({
    sourceContext: searchParams.get('sourceContext') || '',
    documentType: searchParams.get('documentType') || '',
    sourceKind: searchParams.get('sourceKind') || '',
    sourceType: searchParams.get('sourceType') || '',
  });
  const sourceHistoryTitle = getCareSourceScopeTitle(sourceScope);
  const hasScopedHistory = Boolean(
    sourceScope.sourceContext
    || sourceScope.documentType
    || sourceScope.sourceKind
    || sourceScope.sourceType
  );
  const isMedicationSourceHistory = sourceScope.sourceContext === 'medication';
  const { error, hiddenDuplicateCount, loading, sources } = useCareSourceHistory({
    childId: selectedChildId,
    ...sourceScope,
    includeDuplicates: true,
  });

  React.useEffect(() => {
    if (!currentChildId && selectedChildId) {
      setCurrentChildId?.(selectedChildId);
    }
  }, [currentChildId, selectedChildId, setCurrentChildId]);

  const handleReviewTimeline = (target = {}) => {
    navigate('/dashboard', {
      state: {
        timelineEntryId: target.entryId || null,
        selectedDate: target.date instanceof Date ? target.date.toISOString() : null,
      },
    });
  };

  const openMedicationProfile = () => {
    if (!selectedChildId) return;

    setCurrentChildId?.(selectedChildId);
    try {
      window.sessionStorage.setItem('lifelog:last-edit-child-id', selectedChildId);
    } catch (error) {
      // Route state still carries the selected child id.
    }

    navigate(CARE_PROFILE_ROUTE, {
      state: {
        childId: selectedChildId,
        openChildMedicationManager: {
          childId: selectedChildId,
        },
      },
    });
  };

  const handleBack = () => {
    if (isMedicationSourceHistory && selectedChildId) {
      openMedicationProfile();
      return;
    }

    navigate('/dashboard');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F7FAF8', py: { xs: 1.5, md: 3 } }}>
      <Container maxWidth="md">
        <Stack spacing={1.6}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography sx={{ fontSize: { xs: '1.55rem', md: '1.9rem' }, fontWeight: 950, color: colors.semantic.ink, lineHeight: 1.05 }}>
                {sourceHistoryTitle}
              </Typography>
              <Typography sx={{ mt: 0.45, color: alpha(colors.semantic.ink, 0.68), fontSize: '0.95rem' }}>
                {hasScopedHistory
                  ? 'Saved source material for this area.'
                  : 'Saved uploads, long notes, screenshots, and other source material.'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.75} alignItems="center">
              {hasScopedHistory ? (
                <Button
                  variant="text"
                  onClick={() => navigate('/sources')}
                  sx={{ fontWeight: 850, textTransform: 'none' }}
                >
                  All sources
                </Button>
              ) : null}
              <IconButton
                aria-label={isMedicationSourceHistory ? 'Back to medication profile' : 'Back to dashboard'}
                onClick={handleBack}
                sx={{
                  alignSelf: { xs: 'flex-start', sm: 'center' },
                  color: colors.brand.deep,
                  bgcolor: alpha(colors.brand.tint, 0.12),
                  '&:hover': {
                    bgcolor: alpha(colors.brand.tint, 0.2),
                  },
                }}
              >
                <ArrowBackRoundedIcon />
              </IconButton>
            </Stack>
          </Stack>

          {childrenWithAccess.length > 1 ? (
            <Paper
              elevation={0}
              sx={{
                p: 1.2,
                borderRadius: '14px',
                border: `1px solid ${alpha(colors.brand.tint, 0.18)}`,
                bgcolor: '#FFFFFF',
              }}
            >
              <Typography sx={{ mb: 0.7, fontSize: '0.76rem', fontWeight: 850, color: alpha(colors.semantic.ink, 0.64), textTransform: 'uppercase' }}>
                Child
              </Typography>
              <Select
                fullWidth
                size="small"
                value={selectedChildId}
                onChange={(event) => setCurrentChildId?.(event.target.value)}
              >
                {childrenWithAccess.map((child) => (
                  <MenuItem key={child.id} value={child.id}>
                    {child.name || child.displayName || 'Child'}
                  </MenuItem>
                ))}
              </Select>
            </Paper>
          ) : null}

          {error ? (
            <Alert severity="warning">
              We could not load source history right now.
            </Alert>
          ) : null}

          {!error && hiddenDuplicateCount > 0 ? (
            <Alert severity="info" sx={{ py: 0.45 }}>
              {hiddenDuplicateCount} duplicate upload{hiddenDuplicateCount === 1 ? '' : 's'} hidden.
            </Alert>
          ) : null}

          {loading ? (
            <Stack alignItems="center" sx={{ py: 6 }}>
              <CircularProgress size={28} />
              <Typography sx={{ mt: 1, color: alpha(colors.semantic.ink, 0.62), fontWeight: 700 }}>
                Loading sources...
              </Typography>
            </Stack>
          ) : null}

          {!loading && !sources.length ? (
            <Paper
              elevation={0}
              sx={{
                p: 2.2,
                borderRadius: '16px',
                border: `1px solid ${alpha(colors.brand.tint, 0.16)}`,
                bgcolor: '#FFFFFF',
              }}
            >
              <Typography sx={{ fontWeight: 850, color: colors.semantic.ink }}>
                No saved sources yet.
              </Typography>
              <Typography sx={{ mt: 0.4, color: alpha(colors.semantic.ink, 0.64) }}>
                Sources you upload or paste will appear here after they are saved.
              </Typography>
            </Paper>
          ) : null}

          {!loading && sources.map((source) => {
            const status = getCareSourceDisplayStatus(source);
            const chipSx = statusChipStyle(status.tone);
            return (
              <Paper
                key={source.id}
                elevation={0}
                sx={{
                  borderRadius: '16px',
                  border: `1px solid ${alpha(colors.brand.tint, 0.18)}`,
                  bgcolor: '#FFFFFF',
                  overflow: 'hidden',
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ px: { xs: 1.35, sm: 1.6 }, pt: 1.25 }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 900, color: colors.semantic.ink, wordBreak: 'break-word' }}>
                      {getCareSourceTitle(source)}
                    </Typography>
                    {source.duplicateSourceCount > 0 ? (
                      <Typography sx={{ mt: 0.25, fontSize: '0.78rem', color: alpha(colors.semantic.ink, 0.58) }}>
                        {source.duplicateSourceCount} duplicate upload{source.duplicateSourceCount === 1 ? '' : 's'} hidden
                      </Typography>
                    ) : null}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      px: 0.9,
                      py: 0.32,
                      borderRadius: '999px',
                      fontSize: '0.76rem',
                      fontWeight: 850,
                      whiteSpace: 'nowrap',
                      ...chipSx,
                    }}
                  >
                    {status.label}
                  </Box>
                </Stack>
                <Box sx={{ px: { xs: 1.35, sm: 1.6 } }}>
                  <Button onClick={() => navigate(`/sources/${encodeURIComponent(source.id)}`, {state:{returnTo:"/sources"}})} sx={{mx:1,my:0.5}}>Open capture</Button>
                <CareSourceStatusRow
                    source={source}
                    existingMedications={selectedChild?.medicationDetails || []}
                    onReviewTimeline={handleReviewTimeline}
                    onAddMedication={openMedicationProfile}
                  />
                </Box>
              </Paper>
            );
          })}
        </Stack>
      </Container>
    </Box>
  );
};

export default SourceHistoryPage;
